import React, { useState } from 'react';
import ExportDropdown from './ExportDropdown';

/**
 * Outstanding Balances Report Component
 *
 * Displays outstanding balances by client with services provided, uninvoiced amounts,
 * invoiced amounts, client payments, and client balance
 *
 * Features:
 * - Date range picker
 * - "More: 1" filter button
 * - Client name links (clickable)
 * - Table with Services provided, Uninvoiced, Invoiced, Client paid, Client balance columns
 * - Pagination controls
 * - Export functionality
 *
 * TODO: Connect to real patient/appointment data from services
 */

interface OutstandingBalanceData {
  clientName: string;
  clientId: string;
  servicesProvided: number;
  uninvoiced: number;
  invoiced: number;
  clientPaid: number;
  clientBalance: number;
}

interface OutstandingBalancesReportProps {
  onBack?: () => void;
}

const OutstandingBalancesReport: React.FC<OutstandingBalancesReportProps> = ({ onBack }) => {
  const [startDate, setStartDate] = useState('09/16/2025');
  const [endDate, setEndDate] = useState('10/16/2025');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Mock data - TODO: Replace with real data from services
  const mockData: OutstandingBalanceData[] = [];

  const totalRows = mockData.length;
  const startRow = totalRows > 0 ? 1 : 0;
  const endRow = totalRows;

  return (
    <div style={{
      backgroundColor: '#ffffff',
      minHeight: '100vh',
      padding: '1rem'
    }}>
      {/* Sample Data Notice */}
      <div style={{
        backgroundColor: '#FEF3C7',
        border: '1px solid #F59E0B',
        borderRadius: '6px',
        padding: '0.75rem 1rem',
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        fontSize: '0.875rem',
        color: '#92400E'
      }}>
        <span style={{ fontSize: '1.1rem' }}>&#9888;</span>
        <span><strong>Sample Data</strong> — This report will populate with real billing data as appointments and payments are recorded.</span>
      </div>
      {/* Breadcrumb */}
      <div style={{
        fontSize: '0.875rem',
        color: '#d1d5db',
        marginBottom: '1rem'
      }}>
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            color: '#d1d5db',
            textDecoration: 'none',
            cursor: 'pointer',
            padding: 0,
            font: 'inherit'
          }}
        >
          Analytics
        </button>
        {' / '}
        <span style={{ color: '#111827' }}>Outstanding balances</span>
      </div>

      {/* Page Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem'
      }}>
        <h1 style={{
          fontSize: '1.875rem',
          fontWeight: '700',
          color: '#111827',
          margin: 0
        }}>
          Outstanding balances
        </h1>
        <ExportDropdown />
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '1.5rem',
        alignItems: 'center'
      }}>
        {/* Date Range Picker */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          padding: '0.5rem 0.75rem',
          backgroundColor: '#ffffff'
        }}>
          <span style={{ fontSize: '1rem', color: '#2563eb' }}>📅</span>
          <input
            type="text"
            value={`${startDate} - ${endDate}`}
            readOnly
            style={{
              border: 'none',
              outline: 'none',
              fontSize: '0.875rem',
              color: '#111827',
              width: '180px',
              cursor: 'pointer'
            }}
          />
        </div>

        {/* More Filter Button */}
        <button style={{
          backgroundColor: '#ffffff',
          border: '1px solid #2563eb',
          borderRadius: '4px',
          padding: '0.5rem 0.75rem',
          fontSize: '0.875rem',
          color: '#2563eb',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem'
        }}>
          ⋯ More: 1
        </button>
      </div>

      {/* Table */}
      <div style={{
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        overflow: 'hidden',
        marginBottom: '1rem'
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse'
        }}>
          <thead>
            <tr style={{
              backgroundColor: '#f9fafb',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'left',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Client
              </th>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'right',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Services provided
              </th>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'right',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.25rem' }}>
                  Uninvoiced
                  <span style={{ fontSize: '0.875rem', color: '#d1d5db', cursor: 'help' }}>ⓘ</span>
                </div>
              </th>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'right',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Invoiced
              </th>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'right',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Client paid
              </th>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'right',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.25rem' }}>
                  Client balance
                  <span style={{ fontSize: '0.875rem', color: '#d1d5db', cursor: 'help' }}>ⓘ</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Data Rows */}
            {mockData.map((item, index) => (
              <tr key={index} style={{
                borderBottom: index < mockData.length - 1 ? '1px solid #e5e7eb' : 'none',
                backgroundColor: '#ffffff'
              }}>
                <td style={{
                  padding: '0.75rem 1rem',
                  fontSize: '0.875rem'
                }}>
                  <span style={{
                    color: '#2563eb'
                  }}>
                    {item.clientName}
                  </span>
                </td>
                <td style={{
                  padding: '0.75rem 1rem',
                  textAlign: 'right',
                  fontSize: '0.875rem',
                  color: '#d1d5db'
                }}>
                  --
                </td>
                <td style={{
                  padding: '0.75rem 1rem',
                  textAlign: 'right',
                  fontSize: '0.875rem',
                  color: '#111827'
                }}>
                  ${item.uninvoiced}
                </td>
                <td style={{
                  padding: '0.75rem 1rem',
                  textAlign: 'right',
                  fontSize: '0.875rem',
                  color: '#d1d5db'
                }}>
                  --
                </td>
                <td style={{
                  padding: '0.75rem 1rem',
                  textAlign: 'right',
                  fontSize: '0.875rem',
                  color: '#d1d5db'
                }}>
                  --
                </td>
                <td style={{
                  padding: '0.75rem 1rem',
                  textAlign: 'right',
                  fontSize: '0.875rem',
                  color: '#2563eb',
                  fontWeight: '500'
                }}>
                  ${item.clientBalance}
                </td>
              </tr>
            ))}

            {/* Empty State */}
            {mockData.length === 0 && (
              <tr>
                <td colSpan={6} style={{
                  padding: '2rem',
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  color: '#d1d5db'
                }}>
                  No outstanding balances for the selected date range
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '0.875rem',
        color: '#d1d5db'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>Rows per page</span>
          <select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
            style={{
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              padding: '0.25rem 0.5rem',
              fontSize: '0.875rem',
              backgroundColor: '#ffffff'
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span>{startRow}-{endRow} of {totalRows}</span>
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            <button
              disabled={currentPage === 1}
              style={{
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                padding: '0.25rem 0.5rem',
                backgroundColor: '#ffffff',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                color: currentPage === 1 ? '#d1d5db' : '#374151'
              }}
            >
              ⏮
            </button>
            <button
              disabled={currentPage === 1}
              style={{
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                padding: '0.25rem 0.5rem',
                backgroundColor: '#ffffff',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                color: currentPage === 1 ? '#d1d5db' : '#374151'
              }}
            >
              ◀
            </button>
            <button
              disabled={endRow >= totalRows}
              style={{
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                padding: '0.25rem 0.5rem',
                backgroundColor: '#ffffff',
                cursor: endRow >= totalRows ? 'not-allowed' : 'pointer',
                color: endRow >= totalRows ? '#d1d5db' : '#374151'
              }}
            >
              ▶
            </button>
            <button
              disabled={endRow >= totalRows}
              style={{
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                padding: '0.25rem 0.5rem',
                backgroundColor: '#ffffff',
                cursor: endRow >= totalRows ? 'not-allowed' : 'pointer',
                color: endRow >= totalRows ? '#d1d5db' : '#374151'
              }}
            >
              ⏭
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OutstandingBalancesReport;
